import { userServices } from '../../services'
import { updateConfiguration } from '../../global-state/investor-slice'
import type { AppDispatch } from '../../store'
import {
  POST_ONBOARDING_PATH,
  resolveOnboardingPathFromFlags,
  resolveRiskProfilerRequired,
} from './onboarding'
import { resolvePostAuthCampaignDestination } from './campaign-signup-redirect'

export function resolvePostOnboardingDestination(acceptedNDA = false): string {
  return resolvePostAuthCampaignDestination(acceptedNDA) ?? POST_ONBOARDING_PATH
}

export type PostLoginPath =
  | '/onboarding/basic-details'
  | '/onboarding/risk-profiler'
  | '/opportunities'

export type PostLoginNavigation = {
  path: PostLoginPath
  basicDetailsCompleted: boolean
  riskProfileCompleted: boolean
  riskProfilerRequired: boolean
}

export async function getPostLoginNavigation(
  authToken: string,
  _userId?: number,
  _email?: string,
  _fallbackName?: string,
  isSuperuser = false,
): Promise<PostLoginNavigation> {
  try {
    const response = await userServices.getUserProfile_v1({ Authorization: authToken })
    if (response?.status === 200 && response.data) {
      const basicDetailsCompleted = Boolean(
        response.data.basicDetailsCompleted ?? response.data.basic_details_completed,
      )
      const riskProfileCompleted = Boolean(
        response.data.riskProfileCompleted ?? response.data.risk_profile_completed,
      )
      const riskProfilerRequired = resolveRiskProfilerRequired(response.data)

      return {
        path: resolveOnboardingPathFromFlags(
          basicDetailsCompleted,
          riskProfilerRequired,
          isSuperuser,
        ),
        basicDetailsCompleted,
        riskProfileCompleted,
        riskProfilerRequired,
      }
    }
  } catch {
    // fall through to onboarding when profile cannot be loaded
  }

  if (isSuperuser) {
    return {
      path: POST_ONBOARDING_PATH,
      basicDetailsCompleted: false,
      riskProfileCompleted: false,
      riskProfilerRequired: false,
    }
  }

  return {
    path: '/onboarding/basic-details',
    basicDetailsCompleted: false,
    riskProfileCompleted: false,
    riskProfilerRequired: true,
  }
}

type NavigateFn = (path: string) => void

export type PostLoginSession = PostLoginNavigation & { destination: string }

export async function resolvePostLoginDestination(
  authToken: string,
  userId: number,
  email: string,
  name: string,
  acceptedNDA = false,
): Promise<PostLoginSession> {
  const postLogin = await getPostLoginNavigation(authToken, userId, email, name)
  const destination =
    postLogin.path === POST_ONBOARDING_PATH
      ? resolvePostOnboardingDestination(acceptedNDA)
      : postLogin.path

  return { ...postLogin, destination }
}

export async function applyPostLoginNavigation(
  dispatch: AppDispatch,
  navigate: NavigateFn,
  authToken: string,
  userId: number,
  email: string,
  name: string,
  acceptedNDA = false,
): Promise<void> {
  const session = await resolvePostLoginDestination(authToken, userId, email, name, acceptedNDA)
  dispatch(
    updateConfiguration({ name: 'basicDetailsCompleted', value: session.basicDetailsCompleted }),
  )
  dispatch(
    updateConfiguration({ name: 'riskProfileCompleted', value: session.riskProfileCompleted }),
  )
  dispatch(
    updateConfiguration({ name: 'riskProfilerRequired', value: session.riskProfilerRequired }),
  )
  navigate(session.destination)
}
